import React from 'react';
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  ShieldCheck,
  FileCheck2,
  TrendingUp,
  Code2,
  Boxes,
} from 'lucide-react';
import { Reveal, useSheen } from './Reveal';

const iconMap = { FileCheck2, TrendingUp, Code2, Boxes };

// Accent palette per card (used for tag chip, icon chip, bullet checks, CTA ring)
const accent = {
  amber: {
    chipBg: 'linear-gradient(140deg, rgba(245,158,11,0.26) 0%, rgba(245,158,11,0.05) 100%)',
    chipRing: 'ring-amber-500/30',
    chipText: 'text-amber-400',
    tagBg: 'bg-amber-500/15',
    tagText: 'text-amber-300',
    tagBorder: 'border-amber-400/30',
    checkText: 'text-amber-400',
    glow: 'from-amber-500/40 via-transparent to-transparent',
    barGrad: 'from-amber-400 via-amber-500 to-amber-600',
  },
  blue: {
    chipBg: 'linear-gradient(140deg, rgba(96,165,250,0.26) 0%, rgba(96,165,250,0.05) 100%)',
    chipRing: 'ring-blue-500/30',
    chipText: 'text-blue-400',
    tagBg: 'bg-blue-500/15',
    tagText: 'text-blue-300',
    tagBorder: 'border-blue-400/30',
    checkText: 'text-blue-400',
    glow: 'from-blue-500/40 via-transparent to-transparent',
    barGrad: 'from-blue-400 via-blue-500 to-blue-600',
  },
  emerald: {
    chipBg: 'linear-gradient(140deg, rgba(52,211,153,0.26) 0%, rgba(52,211,153,0.05) 100%)',
    chipRing: 'ring-emerald-500/30',
    chipText: 'text-emerald-400',
    tagBg: 'bg-emerald-500/15',
    tagText: 'text-emerald-300',
    tagBorder: 'border-emerald-400/30',
    checkText: 'text-emerald-400',
    glow: 'from-emerald-500/40 via-transparent to-transparent',
    barGrad: 'from-emerald-400 via-emerald-500 to-emerald-600',
  },
  violet: {
    chipBg: 'linear-gradient(140deg, rgba(167,139,250,0.26) 0%, rgba(167,139,250,0.05) 100%)',
    chipRing: 'ring-violet-500/30',
    chipText: 'text-violet-400',
    tagBg: 'bg-violet-500/15',
    tagText: 'text-violet-300',
    tagBorder: 'border-violet-400/30',
    checkText: 'text-violet-400',
    glow: 'from-violet-500/40 via-transparent to-transparent',
    barGrad: 'from-violet-400 via-violet-500 to-violet-600',
  },
};

const ServiceCard = ({ card, delay, onMouseMove }) => {
  const Icon = iconMap[card.icon] || FileCheck2;
  const a = accent[card.color] || accent.amber;

  return (
    <Reveal variant="up" delay={delay} className="h-full">
      <article
        id={card.anchor}
        onMouseMove={onMouseMove}
        data-testid={`service-card-${card.anchor}`}
        className="card-sheen group relative glass-card glass-card-hover rounded-3xl overflow-hidden h-full flex flex-col scroll-mt-28"
      >
        {/* Hero image */}
        <div className="svc-img-wrap relative h-56 lg:h-64">
          <img
            src={card.image}
            alt={card.title}
            loading="lazy"
            className="svc-img w-full h-full object-cover"
          />
          {/* Soft gradient fade for legibility only — no transition */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#070B16] via-[#070B16]/35 to-transparent pointer-events-none" />
          <div
            className={`absolute -inset-x-10 -top-10 h-40 bg-gradient-to-b ${a.glow} opacity-50 blur-3xl pointer-events-none`}
          />

          {/* Top-left: unique service icon badge — embossed, icon-only */}
          <div
            className={`absolute top-4 left-4 w-11 h-11 rounded-2xl flex items-center justify-center ring-1 ${a.chipRing} shadow-lg shadow-black/40 backdrop-blur-xl`}
            style={{ background: a.chipBg }}
            aria-hidden="true"
          >
            <Icon className={`w-5 h-5 ${a.chipText}`} strokeWidth={2.3} />
          </div>

          {/* Top-right tag chip - only when present */}
          {card.tag && (
            <div
              className={`absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full border ${a.tagBg} ${a.tagText} ${a.tagBorder} text-[10px] font-bold tracking-[0.18em] uppercase backdrop-blur-md`}
            >
              {card.tag}
            </div>
          )}

          {/* Bottom accent bar - subtle "progress" strip, grows on hover */}
          <div
            className={`absolute left-0 right-0 bottom-0 h-[3px] bg-gradient-to-r ${a.barGrad} transform origin-left scale-x-[0.3] group-hover:scale-x-100 transition-transform duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)]`}
          />
        </div>

        {/* Content */}
        <div className="relative p-7 lg:p-8 flex flex-col flex-1">
          <h3 className="font-display text-2xl lg:text-[26px] font-bold text-white leading-tight tracking-tight mb-3">
            {card.title}
          </h3>
          <p className="text-slate-300 text-sm leading-relaxed mb-5">{card.desc}</p>

          <ul className="space-y-2.5 mb-7">
            {card.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm text-slate-300 leading-snug">
                <span
                  className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center ring-1 ${a.chipRing}`}
                  style={{ background: a.chipBg }}
                >
                  <Check className={`w-2.5 h-2.5 ${a.checkText}`} strokeWidth={3} />
                </span>
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            data-testid={`service-cta-${card.anchor}`}
            className="mt-auto inline-flex items-center justify-center gap-2 btn-amber rounded-full px-5 py-3 text-sm font-semibold w-full group/btn"
          >
            {card.cta}
            <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-0.5" />
          </a>
        </div>
      </article>
    </Reveal>
  );
};

const Services = ({ data, fullSupport }) => {
  const { onMouseMove } = useSheen();

  return (
    <section id="services" className="relative py-24 lg:py-32 px-6 lg:px-10 scroll-mt-20">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <div>
            <div className="section-num mb-5">
              <span className="num">04</span>
              <span>What We Offer</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight max-w-3xl leading-[1.05]">
              <span className="text-white">A Single Partner for </span>
              <span className="text-gradient-mixed">Procurement, Growth & Technology</span>
            </h2>
            <p className="mt-5 text-slate-400 text-base max-w-2xl leading-relaxed">
              Four tightly integrated service lines — delivered by the same senior team, under one
              NDA, one SLA, and one point of accountability.
            </p>
          </div>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            data-testid="services-overview-cta"
            className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm font-semibold border-b border-blue-400/40 pb-1 self-start lg:self-end"
          >
            Request a Capability Overview
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </Reveal>

        {/* 2x2 grid of premium service cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {data.map((card, i) => (
            <ServiceCard
              key={card.id}
              card={card}
              delay={i * 90}
              onMouseMove={onMouseMove}
            />
          ))}
        </div>

        {/* Full-spectrum support banner */}
        {fullSupport && (
          <Reveal variant="scale" className="mt-10">
            <div
              onMouseMove={onMouseMove}
              className="card-sheen relative glass-card glass-card-hover rounded-3xl overflow-hidden"
            >
              <div className="absolute inset-0">
                <img
                  src={fullSupport.image}
                  alt="Support"
                  className="w-full h-full object-cover opacity-70"
                />
                <div className="themed-fade-r absolute inset-0 bg-gradient-to-r from-[#070B16]/95 via-[#070B16]/55 to-[#070B16]/15" />
              </div>
              <div className="relative p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center gap-6">
                <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl lg:text-3xl font-bold text-white mb-2">
                    {fullSupport.title}
                  </h3>
                  <p className="text-slate-300 max-w-2xl leading-relaxed">{fullSupport.desc}</p>
                </div>
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="inline-flex items-center gap-2 btn-ghost-dark rounded-full px-6 py-3 text-sm whitespace-nowrap"
                >
                  {fullSupport.cta}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};

export default Services;
